import { Nullable } from 'common/models/optional';
import { TransactionUtils } from 'utils/transactionUtils';
import { assign, createMachine } from 'xstate';
import { XStateUtils } from './xStateUtils';
import { ContractTxAsyncActionResponse } from 'common/models/crypto';
import { sendParent } from 'xstate/lib/actions';
import { XStateEvent } from 'common/models/xstate';
import { StorageUtils } from 'utils/storageUtils';
import { SwalUtils } from 'utils/swalUtils';
import { CryptoUtils } from 'utils/cryptoUtils';
import { ChainId } from 'constants/constants';
import { chains } from 'containers/WalletProvider/WalletProvider';

enum TxAction {
  ALERT_EXECUTING_TX = 'ALERT_EXECUTING_TX',
  ALERT_TX_SUCCESS = 'ALERT_TX_SUCCESS',
  ALERT_TX_ERROR = 'ALERT_TX_ERROR',
  TX_CLEANUP = 'TX_CLEANUP',
}
enum TxState {
  BASE_STATE = 'BASE_STATE',
  EXECUTING_TX = 'EXECUTING_TX',
  TX_SUCCESS = 'TX_SUCCESS',
  TX_ERROR = 'TX_ERROR',
  NO_TX_FOUND = 'NO_TX_FOUND',
  CHECK_CONDITIONS = 'CHECK_CONDITIONS',
  NO_WALLET_CONNECTED = 'NO_WALLET_CONNECTED',
  NO_STORED_TX = 'NO_STORED_TX',
}

export enum RemoteTxEvent {
  REMOTE_TX_SUCCESS = 'REMOTE_TX_SUCCESS',
  REMOTE_NO_STORED_TX = 'REMOTE_NO_STORED_TX',
  REMOTE_NO_WALLET_CONNECTED = 'REMOTE_NO_WALLET_CONNECTED',
  REMOTE_TX_ERROR = 'REMOTE_TX_ERROR',
  REMOTE_EXECUTING_TX = 'REMOTE_EXECUTING_TX',
}

// type TxSchema = {
//   states: {
//     [s in TxState]: {};
//   };
// };

// Put this in a separate file
export type RemoteEventExecutingTx =
  XStateEvent<RemoteTxEvent.REMOTE_EXECUTING_TX>;
export type RemoteEventTxError = XStateEvent<
  RemoteTxEvent.REMOTE_TX_ERROR,
  TxErrorEvent
>;
export type RemoteEventTxSuccess = XStateEvent<
  RemoteTxEvent.REMOTE_TX_SUCCESS,
  TxResponseEvent
>;
export type RemoteEventNoWalletConnected =
  XStateEvent<RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED>;
export type RemoteEventNoStoredTx =
  XStateEvent<RemoteTxEvent.REMOTE_NO_STORED_TX>;

type TxEvents =
  | RemoteEventExecutingTx
  | RemoteEventTxError
  | RemoteEventTxSuccess
  | RemoteEventNoWalletConnected
  | RemoteEventNoStoredTx;

type TxContext = {
  accountAddress: Nullable<string>;
  chainId: Nullable<number>;
  functionName: Nullable<string>;
  storedTx: Nullable<StorageUtils.StoredTx>;
  isCheckingForTx: boolean;
  isExecutingTx: boolean;
  txResponse: Nullable<ContractTxAsyncActionResponse>;
  txError: Nullable<Error>;
  // notification: typeof Swal;
};

export const initialTxContext: TxContext = {
  accountAddress: null,
  chainId: null,
  storedTx: null,
  isCheckingForTx: false,
  isExecutingTx: false,
  functionName: null,
  txResponse: null,
  txError: null,
};

type TxResponseEvent = {
  type: string;
  data: ContractTxAsyncActionResponse;
  functionName: string; // dont know if this exists, but is called in many places
};

type TxErrorEvent = {
  type: string;
  data: Error;
};

const machineName = 'txMachine';

export const createTxMachine = (id: string, createWithContext: TxContext) => {
  return createMachine<TxContext, TxEvents>(
    {
      id,
      initial: TxState.CHECK_CONDITIONS,
      context: XStateUtils.shortStringify(createWithContext),
      states: {
        [TxState.BASE_STATE]: {
          invoke: [
            {
              src: {
                type: 'logging',
                state: TxState.BASE_STATE,
              },
            },
          ],
        },
        [TxState.CHECK_CONDITIONS]: {
          invoke: [
            {
              src: {
                type: 'logging',
                state: TxState.CHECK_CONDITIONS,
              },
            },
          ],
          always: [
            {
              target: TxState.NO_WALLET_CONNECTED,
              actions: [sendParent(RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED)],
              // cond: InputUtils.isNullChainId
              cond: (context) => context.chainId == null,
            },
            {
              target: TxState.NO_STORED_TX,
              actions: [sendParent(RemoteTxEvent.REMOTE_NO_STORED_TX)],
              // TODO: I might want to separate this out to NO_STORED_TX and NO_STORED_TX_HASH though maybe that is excessive
              cond: (context) =>
                context.storedTx == null || context.storedTx.hash == null,
            },
            {
              target: TxState.EXECUTING_TX,
              actions: [sendParent(RemoteTxEvent.REMOTE_EXECUTING_TX)],
              cond: () => true,
            },
          ],
        },
        [TxState.EXECUTING_TX]: {
          entry: [
            TxAction.ALERT_EXECUTING_TX,
            assign({
              isExecutingTx: () => true,
            }),
          ],
          exit: assign({
            isExecutingTx: () => false,
          }),
          invoke: [
            {
              src: {
                type: 'logging',
                state: TxState.EXECUTING_TX,
              },
            },
            {
              src: (context) => {
                return TransactionUtils.waitForTx({
                  accountAddress: context.accountAddress,
                  storedTx: context.storedTx as StorageUtils.StoredTx,
                });
              },
              onDone: {
                target: TxState.TX_SUCCESS,
                actions: [
                  assign({
                    txResponse: (_, event: TxResponseEvent) => event.data,
                  }),
                  sendParent((_, event) => ({
                    type: RemoteTxEvent.REMOTE_TX_SUCCESS,
                    value: event.data,
                  })),
                ],
              },
              onError: {
                target: TxState.TX_ERROR,
                actions: [
                  assign({
                    txError: (_, event: TxErrorEvent) => {
                      console.error('TxState.TX_ERROR', event.data);
                      return event.data;
                    },
                  }),
                  sendParent((_, event) => ({
                    type: RemoteTxEvent.REMOTE_TX_ERROR,
                    value: event.data,
                  })),
                ],
              },
            },
          ],
        },
        [TxState.TX_SUCCESS]: {
          entry: [TxAction.ALERT_TX_SUCCESS, TxAction.TX_CLEANUP],
          invoke: [
            {
              src: {
                type: 'logging',
                state: TxState.TX_SUCCESS,
              },
            },
          ],
          meta: {
            // TODO: Make the message based on a future context.displayName
            message: 'Success!',
          },
          type: 'final',
        },
        [TxState.NO_STORED_TX]: {
          entry: [TxAction.ALERT_TX_ERROR],
          invoke: [
            {
              src: {
                type: 'logging',
                state: TxState.NO_STORED_TX,
              },
            },
          ],
          type: 'final',
        },
        [TxState.NO_WALLET_CONNECTED]: {
          entry: [TxAction.ALERT_TX_ERROR, TxAction.TX_CLEANUP],
          invoke: [
            {
              src: {
                type: 'logging',
                state: TxState.NO_WALLET_CONNECTED,
              },
            },
          ],
          meta: {
            message: 'Please connect a wallet!',
          },
        },
        [TxState.TX_ERROR]: {
          entry: [TxAction.ALERT_TX_ERROR, TxAction.TX_CLEANUP],
          invoke: [
            {
              src: {
                type: 'logging',
                state: TxState.TX_ERROR,
              },
            },
          ],
          meta: {
            message: 'Error executing Transaction!',
          },
          type: 'final',
        },
        [TxState.NO_TX_FOUND]: {
          entry: [TxAction.ALERT_TX_ERROR, TxAction.TX_CLEANUP],
          invoke: [
            {
              src: {
                type: 'logging',
                state: TxState.NO_TX_FOUND,
              },
            },
          ],
          meta: {
            // TODO, consider including context.txHash though it may not be necessary
            message: 'No transaction found!',
          },
          type: 'final',
        },
      },
    },
    {
      services: {
        logging:
          (_context, _event, { src: { state } }) =>
          () => {
            if (process.env.NODE_ENV === 'development') {
              console.log(`[${machineName}:state] -> ${state}`);
            }
          },
      },
      actions: {
        // Ok next as well, I don't need to do this in actions, I should do this in state
        // so I need to store the toast in the current context (but think about how that may impact the debugger)
        // THat way I can Toast.update and I can lookinto currentProgressSteps and progressStepsDistance
        [TxAction.ALERT_EXECUTING_TX]: (context) => {
          const url = CryptoUtils.getScannerInfo(
            context?.chainId as number,
            context.storedTx?.hash as string,
            'transaction'
          )?.link;
          void SwalUtils.ToastExecutingTx.fire({
            title: context.storedTx?.labels.executing ?? 'Executing...',
            footer: `View on <a target="_blank" href="${url}" class="ml-5">${CryptoUtils.getScanName(
              context?.chainId as number
            )}</a>`,
          });
        },
        [TxAction.ALERT_TX_SUCCESS]: (context) => {
          const url = CryptoUtils.getScannerInfo(
            context?.chainId as number,
            context.storedTx?.hash as string,
            'transaction'
          )?.link;
          void SwalUtils.ToastSuccessTx.fire({
            title: context.storedTx?.labels.success ?? 'Success!',
            footer: `View on <a target="_blank" href="${url}" class="ml-5">${CryptoUtils.getScanName(
              context?.chainId as number
            )}</a>`,
          });
        },
        [TxAction.ALERT_TX_ERROR]: (context) => {
          void SwalUtils.ToastErrorTx.fire({
            title: context.storedTx?.labels.error ?? 'Error!',
          });
        },
        [TxAction.TX_CLEANUP]: (context) => {
          if (context.storedTx) {
            void StorageUtils.removeTxByHash({
              hash: context.storedTx.hash,
              chainId: context.storedTx.chainId,
              accountAddress: context.storedTx.accountAddress,
            });
          } else {
            console.debug('Could not clean up tx, no stored tx given');
          }
        },
      },
    }
  );
};
